<!-- eslint-disable comma-dangle -->
<!-- eslint-disable indent -->
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { UserTransaction } from "@models/UserTransaction";
import Constants from "@/models/Constants";
import EventCost from "@/components/EventCost.vue";
import LineItem from "@/components/LineItem.vue";
import LineItemOption from "@/components/LineItemOption.vue";
import EventHeader from "@/components/EventHeader.vue";
import AssignExtraCards from "@/components/AssignExtraCards.vue";
import Payment from "@/components/Payment.vue";
import Notification from "@/components/Notification.vue";
import Tooltip from "@/components/Tooltip.vue";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import { useUserProfileStore } from "@/store/UserProfile";
import { useEventParticipantStore } from "@/store/EventParticipant";
import useFormValidation from "@/modules/useFormValidation";
import "vue-step-progress/dist/main.css";
import { Item } from "@models/Item";
import useCheckout from "@/modules/useCheckout";
import { storeToRefs } from "pinia";
import ConnectedAccountDataService from "@/services/ConnectedAccountDataService";
import { EventParticipant } from "@models/EventParticipant";

const route = useRoute();
const userProfileStore = useUserProfileStore();
const eventId = ref("");
const extraCardAssignment = ref<string[]>([]);

const {
  currentPokerRunEvent,
  currentEventParticipants,
  getSelectedLineItems,
  availableLineItems,
  getSelectedAggregatedLineItems,
  canPurchaseAddons,
} = storeToRefs(useEventParticipantStore());
const { loadEventParticipant, getEventParticipantContainer } = useEventParticipantStore();
const optionFeeMap = ref<Map<string, boolean>>(new Map<string, boolean>());
const optionFeeSelectionMap = ref<Map<string, string>>(new Map<string, string>());
const serviceFee = ref({
  name: Constants.SERVICE_FEE,
  amount: 0,
} as Item);
const refpayment = ref(null);

function makeCopies(obj: Item, x: number): Item[] {
  const copies = [];
  for (let i = 0; i < x; i++) {
    copies.push(JSON.parse(JSON.stringify(obj)));
  }
  return copies;
}

const lineItemsWithCost = computed<Item[]>(() => {
  const items = [] as Item[];
  availableLineItems.value.forEach((x) => {
    const quantity = x.quantity ?? 0;
    const newItem = { ...JSON.parse(JSON.stringify(x)) };
    newItem.amount = x.amount * quantity;
    items.push(newItem);
  });
  return items;
});

const {
  stepDetails,
  stepSummary,
  stepPayment,
  stepAssignExtraCards,
  transactionId,
  currentStep,
  isBusy,
  paymentIntentId,
  clientSecret,
  transactionCharge,
  message,
  messageDetails,
  messageType,
  defaultConnectedAccount,
  continueCheckout,
  cancelCheckout,
  calcServiceFee,
  paymentSuccess,
  paymentError,
  paymentInitalized,
} = useCheckout(
  "purchase",
  route.params.id as string,
  refpayment,
  () => {
    if (
      participantsWithoutExtraCards.value.length === extraCardItem.value?.quantity &&
      extraCardItem.value?.quantity > 0
    ) {
      extraCardAssignment.value = participantsWithoutExtraCards.value.map((x) => x.id);
    }

    const items = [] as Item[];
    availableLineItems.value.forEach((x) => {
      if (x.quantity) {
        items.push(...makeCopies(x, x.quantity));
      }
    });

    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      item.participantId = undefined;
      if (index < extraCardAssignment.value.length) {
        item.participantId = extraCardAssignment.value[index];
      }
    }

    return items;
  },
  () => {
    if (currentStep.value === stepDetails) {
      if (
        participantsWithoutExtraCards.value.length === extraCardItem.value?.quantity &&
        extraCardItem.value?.quantity > 0
      ) {
        return stepSummary;
      }
      return stepAssignExtraCards;
    } else if (currentStep.value === stepAssignExtraCards) {
      return stepSummary;
    } else if (currentStep.value === stepSummary) {
      return stepPayment;
    } else {
      return stepDetails;
    }
  },
  route.query.fundraiser as string
);

const extraCardItem = computed<Item | undefined>(() =>
  availableLineItems?.value?.find((x) => x.itemType === Constants.EXTRA_CARD_TYPE)
);

const lineItemsWithServiceFee = computed<Item[]>(() => {
  const fees = [...lineItemsWithCost.value];
  if (serviceFee.value.amount > 0) {
    fees.push(serviceFee.value);
  }
  return fees;
});

const canContinue = computed<boolean>(
  () =>
    !transactionCharge.value.exceedsPreSaleLimit &&
    lineItemsWithServiceFee.value.length > 0 &&
    !lineItemsWithServiceFee.value.some(
      (x) =>
        x.itemOptions &&
        x.itemOptions.length > 0 &&
        (optionFeeSelectionMap.value.get(x.itemId) ?? "") === ""
    )
);

const connectedAccount = computed(() => {
  if (currentPokerRunEvent.value?._internal.connectedAccountDetails?.stripeType === 'legacy') {
    return null;
  }

  return currentPokerRunEvent.value?._internal.connectedAccountDetails?.onboarded ?
    currentPokerRunEvent.value?._internal?.connectedAccountDetails.id :
    defaultConnectedAccount.value;
});


const participantsWithoutExtraCards = ref<EventParticipant[]>([]);

// watch(lineItemsWithCost, async (newVal: Item[], oldVal: Item[]) => {
//   if (newVal.length !== oldVal.length) {
//     await calcServiceFee();
//   }
// });

const extraCardAssignmentUpdated = (assignments: string[]) => {
  extraCardAssignment.value = assignments;
};

if (eventId.value && eventId.value !== route.params.id) {
  eventId.value = route.params.id as string;
} else if (!eventId.value) {
  eventId.value = route.params.id as string;
}

const initialize = async () => {
  UserTransactionDataService.warmup();
  ConnectedAccountDataService.warmup();
  await loadEventParticipant(eventId.value, userProfileStore.profile.id);
  const transactions: UserTransaction[] = await UserTransactionDataService.getUserTransactions(
    userProfileStore.profile.id,
    route.params.id as string
  );
  const transaction = transactions.find(
    (x) => x.status === "pending"
  );
  transactionId.value = transaction?.transactionDetails.transactionId as string;
  paymentIntentId.value = transaction?.transactionDetails.paymentIntentId as string;
  availableLineItems.value.forEach((x) => {
    x.quantity = x.availableQuantity;
  });

  const namedEvents = currentEventParticipants.value.map((x) => {
    return {
      ...x,
      name: `Hand ${currentEventParticipants.value.indexOf(x) + 1}`,
    };
  });

  participantsWithoutExtraCards.value = namedEvents.filter((x) => !x.extraCard);
  await calcServiceFee();
};

initialize();
</script>
<template>
  <AppEventContainer :key="`register-${eventId}`">
    <Notification class="is-size-5 my-2" :message="message" :details="messageDetails" :message-type="messageType" />
    <div class="p-2">
      <div class="is-flex">
        <div class="icon mx-1"></div>
        <EventHeader class="pb-2 is-flex-grow-1" :poker-run-event="currentPokerRunEvent" :show-type="true">
        </EventHeader>
        <Tooltip class="is-pulled-right" tool-tip-key="EventRegistration"></Tooltip>
      </div>

      <div v-if="availableLineItems">
        <div class="pb-2" v-show="currentStep === stepDetails">
          <div v-show="currentPokerRunEvent && availableLineItems.length === 0">
            <p class="has-text-weight-semibold">No add-ons available for this event.</p>
          </div>
          <template v-for="(item, index) in availableLineItems" :key="`optional-${index}`">
            <div v-show="currentStep === stepDetails">
              <div class="px-2 mt-2 is-flex is-justify-content-space-between has-background-primary has-text-white">
                <div>Item</div>
                <div>QTY</div>
              </div>

              <div class="p-2 is-flex is-justify-content-space-between">
                <div>
                  <p>{{ item.name }}</p>
                  <p>
                    {{
                      item?.amount.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    }}
                  </p>
                </div>
                <div>
                  <AppNumberSelect v-model="item.quantity" :max="item.availableQuantity" />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <AssignExtraCards v-show="currentStep === stepAssignExtraCards" v-if="extraCardItem"
        :event-participants="participantsWithoutExtraCards" :quantity="extraCardItem?.quantity"
        @assignment-updated="extraCardAssignmentUpdated"></AssignExtraCards>

      <EventCost class="pb-2" v-show="currentStep === stepSummary" :line-items="lineItemsWithCost"
        :total-amount="transactionCharge.subtotal"></EventCost>

      <div class="pb-2" v-if="currentStep === stepPayment">
        <Payment ref="refpayment" :client-secret="clientSecret" :line-items="lineItemsWithCost"
          :connected-account-id="connectedAccount" @error="paymentError" @success="paymentSuccess"
          @initailized="paymentInitalized"></Payment>
      </div>

      <div class="is-size-4 has-text-semibold" v-if="transactionCharge.exceedsPreSaleLimit">
        This event is unable to accept payments at this time. Please try again later.
      </div>

      <AppContinueCancel :is-busy="isBusy" :is-disabled="!canContinue" @continue="continueCheckout"
        @cancel="cancelCheckout">
      </AppContinueCancel>
    </div>
  </AppEventContainer>
</template>
